import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const registrationApplicationsApiSlice = createApi({
  reducerPath: "applicationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Applications"],
  endpoints: (builder) => ({
    applications: builder.query({
      query: ({ endpoint, id, search, status, filters = {} }) => {
        const url = id ? `${endpoint}${id}` : endpoint;
        // Build query parameters
        const params = {};
        if (search && search.trim() !== "") params.search = search.trim();

        if (status && status !== "all") {
          params.status = status;
        }

        if (filters.searchNormalized) {
          params.searchNormalized = filters.searchNormalized;
        }
        if (filters.auction) {
          params.auction = filters.auction;
        }
        if (filters.paymentMethod) {
          params.paymentMethod = filters.paymentMethod;
        }
        if (filters.customerId) {
          params.customerId = filters.customerId;
        }
        if (filters.buyerId) {
          params.buyerId = filters.buyerId;
        }

        const finalQuery = {
          url,
          method: "GET",
          params: Object.keys(params).length > 0 ? params : undefined,
        };

        // Debug API call
        console.log("API Query Debug:");
        console.log("- endpoint:", endpoint);
        console.log("- id:", id);
        console.log("- search:", search);
        console.log("- status:", status);
        console.log("- filters:", filters);
        console.log("- final url:", url);
        console.log("- params:", params);
        console.log("- finalQuery:", finalQuery);

        return finalQuery;
      },
      keepUnusedDataFor: 0, // Don't cache search results
      providesTags: (result, error, { search, status, filters = {} }) => {
        const baseTags = [{ type: "Applications", id: "LIST" }];
        
        if (result?.applications) {
          const applicationTags = result.applications.map(({ _id }) => ({
            type: "Applications",
            id: _id,
          }));
          
          // Add search-specific tags to ensure proper cache invalidation
          const searchTag = search ? { type: "Applications", id: `SEARCH-${search}` } : null;
          const statusTag = status ? { type: "Applications", id: `STATUS-${status}` } : null;
          const filterTag = Object.keys(filters).length
            ? { type: "Applications", id: `FILTERS-${JSON.stringify(filters)}` }
            : null;
          
          return [
            ...applicationTags,
            ...baseTags,
            ...(searchTag ? [searchTag] : []),
            ...(statusTag ? [statusTag] : []),
            ...(filterTag ? [filterTag] : []),
          ];
        }
        
        return baseTags;
      },
    }),

    createApplication: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [{ type: "Applications", id: "LIST" }],
    }),

    updateApplication: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Applications", id },
        { type: "Applications", id: "LIST" },
      ],
    }),

    deleteApplication: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
      }),
      invalidatesTags: [{ type: "Applications", id: "LIST" }],
    }),
  }),
});

export const {
  useApplicationsQuery,
  useUpdateApplicationMutation,
  useCreateApplicationMutation,
  useDeleteApplicationMutation,
} = registrationApplicationsApiSlice;
